import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { customersAPI, freeOffersAPI } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Save,
  Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";

export default function FreeOfferEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [offer, setOffer] = useState(null);
  
  const [formData, setFormData] = useState({
    customer_id: "",
    title: "",
    payment_days: 14,
    include_vat: true,
    vat_rate: 19,
    notes: "",
    show_item_prices: true
  });
  
  const [items, setItems] = useState([
    { description: "", quantity: 1, unit: "Stück", unit_price: 0 }
  ]);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [customersRes, offerRes] = await Promise.all([
        customersAPI.list(),
        freeOffersAPI.get(id)
      ]);
      
      setCustomers(customersRes.data);
      setOffer(offerRes.data);
      
      // Check if offer is editable
      if (offerRes.data.status === "finalisiert") {
        toast.error("Finalisierte Angebote können nicht bearbeitet werden");
        navigate(`/freitext-angebote/${id}`);
        return;
      }
      
      // Populate form with existing data
      setFormData({
        customer_id: offerRes.data.customer_id || "",
        title: offerRes.data.title || "Freitext-Angebot",
        payment_days: offerRes.data.payment_days || 14,
        include_vat: offerRes.data.include_vat !== false,
        vat_rate: offerRes.data.vat_rate ?? 19,
        notes: offerRes.data.notes || "",
        show_item_prices: offerRes.data.show_item_prices !== false
      });
      
      // Populate items
      if (offerRes.data.items && offerRes.data.items.length > 0) {
        setItems(offerRes.data.items.map(item => ({
          description: item.description || "",
          quantity: item.quantity || 1,
          unit: item.unit || "Stück",
          unit_price: item.unit_price || 0
        })));
      }
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Fehler beim Laden der Daten");
      navigate("/angebote");
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setItems([...items, { description: "", quantity: 1, unit: "Stück", unit_price: 0 }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => {
      return sum + (item.quantity * item.unit_price);
    }, 0);
    const vatAmount = formData.include_vat ? subtotal * (formData.vat_rate / 100) : 0;
    const total = subtotal + vatAmount;
    return { subtotal, vatAmount, total };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_id) {
      toast.error("Bitte wählen Sie einen Kunden aus");
      return;
    }
    
    if (items.some(item => !item.description || item.unit_price <= 0)) {
      toast.error("Bitte füllen Sie alle Positionen aus (Beschreibung und Preis)");
      return;
    }
    
    setSaving(true);
    try {
      await freeOffersAPI.update(id, {
        ...formData,
        items: items.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity) || 1,
          unit: item.unit,
          unit_price: parseFloat(item.unit_price) || 0
        }))
      });
      
      toast.success("Angebot wurde aktualisiert");
      navigate(`/freitext-angebote/${id}`);
    } catch (error) {
      console.error("Failed to update offer:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR"
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const { subtotal, vatAmount, total } = calculateTotals();

  return (
    <div className="space-y-6" data-testid="free-offer-edit-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold font-['Manrope']">
              Angebot bearbeiten
            </h1>
            <p className="text-muted-foreground">{offer?.offer_number}</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Kunde</CardTitle>
              </CardHeader>
              <CardContent>
                <Select
                  value={formData.customer_id}
                  onValueChange={(value) => setFormData({ ...formData, customer_id: value })}
                >
                  <SelectTrigger data-testid="customer-select">
                    <SelectValue placeholder="Kunde auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.company_name || `${customer.first_name} ${customer.last_name}`}
                        {customer.customer_number && ` (${customer.customer_number})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {/* Title */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Angebotsinformationen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="title">Titel</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="z.B. Angebot Solaranlage"
                    data-testid="title-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Line Items */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Positionen</CardTitle>
                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                  <Plus className="h-4 w-4 mr-2" />
                  Position hinzufügen
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-3 items-end">
                    <div className="col-span-5">
                      {index === 0 && <Label className="text-xs text-muted-foreground">Beschreibung</Label>}
                      <Textarea
                        value={item.description}
                        onChange={(e) => updateItem(index, "description", e.target.value)}
                        placeholder="Beschreibung der Leistung"
                        className="min-h-[60px]"
                        data-testid={`item-description-${index}`}
                      />
                    </div>
                    <div className="col-span-2">
                      {index === 0 && <Label className="text-xs text-muted-foreground">Menge</Label>}
                      <Input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, "quantity", parseFloat(e.target.value) || 0)}
                        data-testid={`item-quantity-${index}`}
                      />
                    </div>
                    <div className="col-span-2">
                      {index === 0 && <Label className="text-xs text-muted-foreground">Einheit</Label>}
                      <Select
                        value={item.unit}
                        onValueChange={(value) => updateItem(index, "unit", value)}
                      >
                        <SelectTrigger data-testid={`item-unit-${index}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Stück">Stück</SelectItem>
                          <SelectItem value="Stunden">Stunden</SelectItem>
                          <SelectItem value="Tage">Tage</SelectItem>
                          <SelectItem value="Pauschal">Pauschal</SelectItem>
                          <SelectItem value="kWp">kWp</SelectItem>
                          <SelectItem value="m²">m²</SelectItem>
                          <SelectItem value="Meter">Meter</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      {index === 0 && <Label className="text-xs text-muted-foreground">Einzelpreis</Label>}
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) => updateItem(index, "unit_price", parseFloat(e.target.value) || 0)}
                        data-testid={`item-price-${index}`}
                      />
                    </div>
                    <div className="col-span-1">
                      {items.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeItem(index)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Notes */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Anmerkungen</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Optionale Anmerkungen zum Angebot..."
                  className="min-h-[100px]"
                  data-testid="notes-input"
                />
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* VAT Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">MwSt.-Einstellungen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="include_vat">MwSt. ausweisen</Label>
                  <Switch
                    id="include_vat"
                    checked={formData.include_vat}
                    onCheckedChange={(checked) => setFormData({ ...formData, include_vat: checked })}
                  />
                </div>
                {formData.include_vat && (
                  <div>
                    <Label htmlFor="vat_rate">MwSt.-Satz (%)</Label>
                    <Select
                      value={String(formData.vat_rate)}
                      onValueChange={(value) => setFormData({ ...formData, vat_rate: parseFloat(value) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0%</SelectItem>
                        <SelectItem value="7">7%</SelectItem>
                        <SelectItem value="19">19%</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="flex items-start justify-between pt-2 border-t">
                  <div className="pr-3">
                    <Label htmlFor="show_item_prices">Einzelpreise im PDF ausweisen</Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Wenn deaktiviert, werden im PDF (Angebot, Auftragsbestätigung) nur Position, Beschreibung und Menge angezeigt — keine Einzel-/Gesamtpreise. Die Gesamtsumme steht am Ende.
                    </p>
                  </div>
                  <Switch
                    id="show_item_prices"
                    checked={formData.show_item_prices}
                    onCheckedChange={(checked) => setFormData({ ...formData, show_item_prices: checked })}
                    data-testid="show-item-prices-switch"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Payment Terms */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Zahlungsbedingungen</CardTitle>
              </CardHeader>
              <CardContent>
                <div>
                  <Label htmlFor="payment_days">Zahlungsziel (Tage)</Label>
                  <Input
                    id="payment_days"
                    type="number"
                    min="0"
                    value={formData.payment_days}
                    onChange={(e) => setFormData({ ...formData, payment_days: parseInt(e.target.value) || 14 })}
                    data-testid="payment-days-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Zusammenfassung</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Netto</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>
                {formData.include_vat && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">MwSt. ({formData.vat_rate}%)</span>
                    <span>{formatCurrency(vatAmount)}</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between font-bold">
                  <span>Gesamt</span>
                  <span className="text-lg">{formatCurrency(total)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Submit Button */}
            <Button 
              type="submit" 
              className="w-full" 
              size="lg"
              disabled={saving}
              data-testid="save-offer-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Speichern...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Änderungen speichern
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
